import { useAuthReady } from './useAuthReady';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchReferral, patchReferralMode, type ReferralRewardMode } from '../lib/referral';

/** The user's invite link, earnings, and payout preference. */
export function useReferral(enabled = true) {
  const { canFetch, initData, cacheUserId: userId } = useAuthReady();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['referral', userId],
    enabled: enabled && canFetch,
    queryFn: () => fetchReferral(initData),
  });

  const setMode = useMutation({
    mutationFn: (rewardMode: ReferralRewardMode) => patchReferralMode(initData, rewardMode),
    onSuccess: (data) => {
      qc.setQueryData(['referral', userId], (prev: typeof q.data) =>
        prev ? { ...prev, rewardMode: data.rewardMode } : prev,
      );
    },
  });

  return {
    code: q.data?.code ?? null,
    link: q.data?.link ?? null,
    invitedCount: q.data?.invitedCount ?? 0,
    invited: q.data?.invited ?? [],
    rewardMode: q.data?.rewardMode ?? null,
    enabled: q.data?.enabled ?? true,
    rewardPct: q.data?.rewardPct ?? null,
    vaults: q.data?.vaults ?? [],
    earnedUsd: q.data?.earnedUsd ?? '0.00',
    earnedIncpt: q.data?.earnedIncpt ?? '0',
    earnedLiquidUsd: q.data?.earnedLiquidUsd ?? '0.00',
    recentRewards: q.data?.recentRewards ?? [],
    setRewardMode: setMode.mutateAsync,
    settingMode: setMode.isPending,
    loading: q.isLoading,
    error: q.isError,
    refetch: () => q.refetch(),
  };
}
